import React, {useState, useContext} from 'react';
import FISACoolingOffCancellationMessage from './FISACoolingOffCancellationMessage';
import FISAPenaltyNotice from './AccountClosure/FISA/Penalty/FISAPenaltyNotice';
import InterestPaidTo from './InterestPaidTo';
import {customerDataContext} from '../../contexts/CustomerDataProvider';
import {useUnifiedAnalytics} from '../../contexts/AnalyticsWrapper';
import {useLogger} from '../../contexts/Logger';
import Loading from '../Loading/Loading';
import AccountClosure from './AccountClosure/AccountClosure';
import FrsTerm from './FrsTerm';
import FrsFundingWindow from './FrsFundingWindow';
import chevDown from '../../assets/images/chev-down-slim.svg';
import chevUp from '../../assets/images/chev-up.svg';
import NsAccountInfo from './TabContent/NsAccountInfo';
import NsAccountCancelClose from './TabContent/NsAccountCancelClose';
import {productKeys, productTypes} from "../../utils/productHelpers";
import withFeatureFlags from "../FeatureFlags/withFeatureFlags";
import TransferOutCancellationModal from './TransferOutCancellationModal';
import { IdsSectionNotice } from '@investec/ids-web-react';

/** @import {Account, Customer, CustomerDataContextType, NewAccount, PendingTransaction} from '../../contexts/CustomerDataProviderTypes' */
/** @import {MyAccountProps, AccountTabGroupProps, FrsProductData, OfsProfileData, FisaProductData, DestinationAccount, NsProfileData, NsCancelCloseData} from './MyAccountTypes' */
import './myAccount.scss'
import {MaturityInstruction} from "./MaturityInstruction/MaturityInstruction";
import FisaTerm from "./FisaTerm";
import FisaFundingWindow from "./FisaFundingWindow";
import Button from "../Form/Button/Button";
import InstantAccessCashIsaDetails from './InstantAccessCashIsaDetails';

// Transfer status helpers (kept locally to avoid changing shared modules)
const TRANSFER_STATUS_LABELS = /** @type {Record<string, string>} */ ({
    Requested: 'Requested',
    InProgress: 'In progress',
    Completed: 'Completed',
    Unsuccessful: 'Unsuccessful',
    CancellationRequested: 'Cancellation requested',
    Cancelled: 'Cancelled',
});

const TRANSFER_STATUS_MODIFIERS = /** @type {Record<string, string>} */ ({
    Requested: 'requested',
    InProgress: 'in-progress',
    Completed: 'completed',
    Unsuccessful: 'unsuccessful',
    CancellationRequested: 'cancellation-requested',
    Cancelled: 'cancelled',
});

/**
 * @param {string | null | undefined} status
 * @returns {string}
 */
function normalizeTransferStatus(status) {
    if (!status) return '';

    const collapsedStatus = String(status).trim().replace(/[\s_-]+/g, '').toLowerCase();
    const normalizedStatusMap = /** @type {Record<string, string>} */ ({
        requested: 'Requested',
        inprogress: 'InProgress',
        completed: 'Completed',
        unsuccessful: 'Unsuccessful',
        cancellationrequested: 'CancellationRequested',
        cancelled: 'Cancelled',
    });

    return normalizedStatusMap[collapsedStatus] || String(status).trim();
}

/**
 * @param {string | null | undefined} status
 * @returns {string}
 */
function getTransferStatusLabel(status) {
    const normalizedStatus = normalizeTransferStatus(status);
    return TRANSFER_STATUS_LABELS[normalizedStatus] || normalizedStatus;
}

/**
 * @param {string | null | undefined} status
 * @returns {string}
 */
function getTransferStatusModifier(status) {
    const normalizedStatus = normalizeTransferStatus(status);
    return TRANSFER_STATUS_MODIFIERS[normalizedStatus]
        || normalizedStatus.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function OfsAccountTab(/** @type {{profileData: OfsProfileData}} */ props) {
    const profileData = props.profileData;
    return (
        <React.Fragment>
            <div className="my-account__content my-account__content--small-margin-bottom">
                <div className="my-account__left my-account__doubleCol">
                    <InterestPaidTo profile={profileData} />
                </div>
            </div>
            <div className="my-account__content my-account__content--small-margin-bottom">
                <div className="my-account__left">
                    <AccountClosure
                        productName={profileData.productName}
                        mobileNumber={profileData.mobileNumber}
                        accountId={profileData.accountId}
                        accountStatus={profileData.accountStatus} />
                </div>
            </div>
        </React.Fragment>
    )
};

function FrsAccountTab(/** @type {{profileData: FrsProductData;}} */ props) {
    if (!props.profileData.fundingStartDate)
        return <></>;
    return (
        <div className="my-account--account-detail-box my-account__content--small-margin-bottom">
            
            <MaturityInstruction profileData={props.profileData} />
            
            <div className="my-account__row">
                <div className="my-account__col my-account__col">
                    <FrsTerm profileData={props.profileData} />
                </div>
                {!props.profileData.maturityDate &&
                    <div className="my-account__col my-account__doubleCol my-account__col--full-width my-account__col--small-margin-left">
                        <FrsFundingWindow profileData={props.profileData} />
                    </div>
                }
            </div>
            {props.profileData.instruction && props.profileData.maturityWindowStatus === 'InstructionOpen' &&
                <p className="maturity3__aer-info">
                    <br /><br />
                    * AER stands for the Annual Equivalent Rate and illustrates
                    what the interest rate would be if interest was paid and compounded once a year. The AER is
                    intended
                    to be
                    an indicative rate to help you compare the return on
                    different savings products.
                </p>}
        </div>
    )
};

function FisaAccountTab(/** @type {{profileData: FisaProductData;}} */ props) {
    if (!props.profileData.fundingStartDate)
        return <></>;

    const analytics = useUnifiedAnalytics();
    const logger = useLogger();
    const [showTransferOutCancellationModal, setShowTransferOutCancellationModal] = useState(false);

    // Get cooling-off end date from account data (same as FisaAccountClosure)
    const coolingOffEndDate = props.profileData.maturingAccount?.coolingOffEndDate;
    const now = new Date();
    
    // Compare only the date parts (not time) to determine if cooling-off period is still active
    // If today's date is less than or equal to the cooling-off end date, show cooling-off message
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const coolingOffEnd = coolingOffEndDate ? new Date(new Date(coolingOffEndDate).getFullYear(), new Date(coolingOffEndDate).getMonth(), new Date(coolingOffEndDate).getDate()) : null;
    const isInCoolingOffPeriod = coolingOffEnd && today <= coolingOffEnd;

    // Format the end date for display
    const formattedEndDate = coolingOffEndDate 
        ? `${new Date(coolingOffEndDate).toLocaleDateString(undefined, { day: '2-digit', month: 'long', year: 'numeric' })}`
        : '';

    const handleCloseAccountClick = () => {
        const closureType = isInCoolingOffPeriod ? 'cooling-off' : 'penalty';
        const flowName = `fisa ${closureType} account closure`;

        logger.info(`User initiated ${closureType} ISA closure journey`, {
            additionalData: {
                accountId : props.profileData.accountId
            }
        });        
        
        analytics.trackAnalyticsClickEvent(
            flowName,
            window.location.href,
            'my-account',
            'fisa_1yr',
            '',
            {},
            'button click',
            `manage_account|button_click|close_fisa_account`,
            'manage_account'
        );
        
        window.location.pathname = "close-account/" + props.profileData.accountId;
    };

    const transferIn = props.profileData.transferIn;
    const transferInStatusNormalized = transferIn ? normalizeTransferStatus(transferIn.status) : '';
    const hasTransferInProgress = transferInStatusNormalized === 'Requested' || transferInStatusNormalized === 'InProgress';
    const transferInLastUpdatedLabel = transferIn?.lastUpdatedAt
        ? new Date(transferIn.lastUpdatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
        : '';

    const transferOut = props.profileData.transferOut;
    const transferOutStatusNormalized = transferOut ? normalizeTransferStatus(transferOut.status) : '';
    const hasTransferOutInProgress = transferOutStatusNormalized === 'Requested' || transferOutStatusNormalized === 'InProgress';
    const hasTransferOutCancellationRequested = transferOutStatusNormalized === 'CancellationRequested';
    const isTransferOutStillActive = hasTransferOutInProgress || hasTransferOutCancellationRequested;

    const handleCancelTransferClick = () => {
        analytics.trackAnalyticsClickEvent(
            'fisa transfer out cancel request',
            window.location.href,
            'my-account',
            'fisa_1yr',
            '',
            {},
            'button click',
            `manage_account|button_click|cancel_transfer_request`,
            'manage_account'
        );

        setShowTransferOutCancellationModal(true);
    };

    const handleTransferOutCancellationRequested = (updatedAt = new Date().toISOString()) => {
        if (!props.profileData.transferOut) {
            return;
        }

        props.profileData.transferOut.status = 'CancellationRequested';
        props.profileData.transferOut.lastUpdatedAt = updatedAt;
    };

    const closeTransferOutCancellationModal = () => {
        setShowTransferOutCancellationModal(false);
    };

    const isCloseButtonDisabled = props.profileData.accountStatus === 'CLOSURE IN PROGRESS'
        || props.profileData.accountStatus === 'CLOSED'
        || hasTransferInProgress
        || hasTransferOutInProgress
        || hasTransferOutCancellationRequested;
    const shouldShowClosureInfoNotice = props.profileData.accountStatus !== 'CLOSED'
        && props.profileData.accountStatus !== 'ClosureRequested'
        && props.profileData.accountStatus !== 'CLOSURE IN PROGRESS';

    return (
        <div className="my-account--account-detail-box my-account__content--small-margin-bottom">
            <MaturityInstruction profileData={props.profileData} />

            <div className="my-account__row">
                <div className="my-account__col my-account__col">
                    <FisaTerm profileData={props.profileData} />
                </div>
            </div>
            <div className="my-account__row">
                {!props.profileData.maturityDate &&
                    <div className="my-account__col my-account__col">
                        <FisaFundingWindow profileData={props.profileData} />
                    </div>
                }
            </div>
            {/* Show cooling-off message while active; hide early-closure notice when account closure is unavailable */}
            {isInCoolingOffPeriod ? (
                <div className="my-account__row">
                    <div className="my-account__col">
                        <FISACoolingOffCancellationMessage endDate={formattedEndDate} />
                    </div>
                </div>
            ) : shouldShowClosureInfoNotice ? (
                <div className="my-account__row">
                    <div className="my-account__col">
                        <FISAPenaltyNotice 
                            earlyClosureFee={props.profileData.closureFee?.toFixed(2) || '0.00'}
                            originalBalance={props.profileData.balance}
                            interestRate={props.profileData.interestRate}
                            productName={props.profileData.productName}
                        />
                    </div>
                </div>
            ) : null}
            <div className="my-account__row">
                <div className="my-account__col my-account__col">
                    <Button
                        size='med'
                        label={`Close my ${props.profileData.productName}`}
                        onClick={handleCloseAccountClick}
                        disabled={isCloseButtonDisabled}
                    />
                </div>
            </div>
            {hasTransferInProgress && (
                <div className="my-account__row my-account__row--transfer-disabled-message">
                    <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                        <IdsSectionNotice appearance="information" className="my-account__fisa-transfer-notice">
                            You&apos;ll need to cancel your ISA transfer before you can close this account
                        </IdsSectionNotice>
                    </div>
                </div>
            )}
            {hasTransferInProgress && transferIn && (
                <div className="my-account__transfer-section">
                    <div className="my-account__row my-account__row--transfer-request-header">
                        <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                            <hr className="my-account__transfer-divider" />
                            <h2 className="my-account__transfer-section-title">Your ISA transfer request</h2>
                            <div className="my-account__transfer-request-row">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="my-account__transfer-request-arrow" aria-hidden="true">
                                    <path d="M12 4C12.5523 4 13 4.44772 13 5V16.5858L18.2929 11.2929C18.6834 10.9024 19.3166 10.9024 19.7071 11.2929C20.0976 11.6834 20.0976 12.3166 19.7071 12.7071L12.7071 19.7071C12.3166 20.0976 11.6834 20.0976 11.2929 19.7071L4.29289 12.7071C3.90237 12.3166 3.90237 11.6834 4.29289 11.2929C4.68342 10.9024 5.31658 10.9024 5.70711 11.2929L11 16.5858V5C11 4.44772 11.4477 4 12 4Z" fill="#489759"/>
                                </svg>
                                <span className="my-account__transfer-request-label">Transfer in</span>
                                <span className={`my-account__transfer-request-tag my-account__transfer-request-tag--${transferIn.status === 'InProgress' ? 'in-progress' : transferIn.status.toLowerCase()}`}>
                                    {transferIn.status === 'InProgress' ? 'In progress' : transferIn.status}
                                </span>
                                <span className="my-account__transfer-request-date">
                                    last updated {new Date(transferIn.lastUpdatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="my-account__row my-account__row--transfer-request-message">
                        <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                            <IdsSectionNotice
                                appearance="information"
                                className="my-account__fisa-transfer-notice"
                                title="Cancelling your transfer"
                            >
                                To cancel this transfer, please contact your existing ISA provider. If they&apos;re able to do this, they will let us know and we will update your account.
                            </IdsSectionNotice>
                        </div>
                    </div>
                </div>
            )}

            {isTransferOutStillActive && transferOut && (
                <div className="my-account__transfer-section">
                    <div className="my-account__row my-account__row--transfer-request-header">
                        <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                            <hr className="my-account__transfer-divider" />
                            <h2 className="my-account__transfer-section-title">Your ISA transfer request</h2>
                            <div className="my-account__transfer-request-row">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="my-account__transfer-request-arrow" aria-hidden="true">
                                    <path d="M11.2929 4.29289C11.6834 3.90237 12.3166 3.90237 12.7071 4.29289L19.7071 11.2929C20.0976 11.6834 20.0976 12.3166 19.7071 12.7071C19.3166 13.0976 18.6834 13.0976 18.2929 12.7071L13 7.41421V19C13 19.5523 12.5523 20 12 20C11.4477 20 11 19.5523 11 19V7.41421L5.70711 12.7071C5.31658 13.0976 4.68342 13.0976 4.29289 12.7071C3.90237 12.3166 3.90237 11.6834 4.29289 11.2929L11.2929 4.29289Z" fill="#B3393F"/>
                                </svg>
                                <span className="my-account__transfer-request-label">Transfer out</span>
                                <span className={`my-account__transfer-request-tag my-account__transfer-request-tag--${getTransferStatusModifier(transferOut.status)}`}>
                                    {getTransferStatusLabel(transferOut.status)}
                                </span>
                                <span className="my-account__transfer-request-date">
                                    last updated {new Date(transferOut.lastUpdatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="my-account__row my-account__row--transfer-request-message">
                        <div className="my-account__col my-account__col--full-width my-account__transfer-col">
                            <IdsSectionNotice
                                appearance={hasTransferOutCancellationRequested ? 'warning' : 'information'}
                                className="my-account__transfer-out-ids-notice"
                                title={hasTransferOutCancellationRequested ? getTransferStatusLabel(transferOut.status) : 'Transfer out in progress'}
                            >
                                {hasTransferOutCancellationRequested ? (
                                    <>
                                        <p>You&apos;ve instructed us to cancel this transfer. We&apos;ve contacted your other ISA provider and we&apos;ll let you know when cancellation is confirmed.</p>
                                        <p>We&apos;ll also let you know if the cancellation has been unsuccessful and what to do next.</p>
                                    </>
                                ) : (
                                    <>
                                        <p>You can cancel this transfer at any time. If you do, we&apos;ll contact your other ISA provider and update you once the cancellation is confirmed.</p>
                                        <p>This transfer will show as &apos;in progress&apos; until confirmed. You won&apos;t be able to close this account while a transfer is in progress.</p>
                                    </>
                                )}
                            </IdsSectionNotice>
                        </div>
                    </div>

                    <div className="my-account__row">
                        <div className="my-account__col my-account__col">
                            <Button
                                size='med'
                                label={hasTransferOutCancellationRequested ? getTransferStatusLabel(transferOut.status) : 'Cancel my transfer request'}
                                onClick={handleCancelTransferClick}
                                disabled={hasTransferOutCancellationRequested || showTransferOutCancellationModal}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showTransferOutCancellationModal && (
                <TransferOutCancellationModal
                    accountId={props.profileData.accountId}
                    mobileNumber={props.profileData.mobileNumber}
                    onCancel={closeTransferOutCancellationModal}
                    onComplete={closeTransferOutCancellationModal}
                    onCancellationRequested={handleTransferOutCancellationRequested}
                />
            )}

        </div>
    )
};

function getOfsProfileData(/** @type {Customer<NewAccount>} */ customer, /** @type {Account} */ acc) {
    const ofsProfileData = {
        interestPaidTo: acc.interestPaid,
        mobileNumber: customer.mobileNumber,
        accountId: acc.id,
        accountStatus: acc.accountStatus,
        productName: acc.product.name
    };
    return ofsProfileData;
};

/** @returns {NsProfileData} */
function getNsProfileData(/** @type {Customer<NewAccount>} */ customer, /** @type {Account} */ acc) {
    const nsProfileData = {
        interestPaidTo: acc.interestPaid,
        mobileNumber: customer.mobileNumber,
        accountId: acc.id,
        accountStatus: acc.accountStatus,
        accountNumber: acc.accountNumber,
        productName: acc.product.name,
        noticePeriod: acc.notice?.noticePeriod,
        noticePeriodUnit: acc.notice?.noticePeriodUnit,
        destinationAccounts: getDestinationAccounts(acc),
        balance: acc.balance
    };
    return nsProfileData;
};

/** @returns {NsCancelCloseData} */
function buildNsCancelCloseData(/** @type {Customer<NewAccount>} */ _customer, /** @type {Account} */ acc, /** @type {PendingTransaction} */ pendingTransaction) {
    return {
        accountId: acc.id,
        accountNumber: acc.accountNumber,
        productName: acc.product.name,
        pendingTransaction: pendingTransaction
    };
};

function getPendingTransactionTaggedForClosing(/** @type {Account} */ acc) {
    return acc.pendingTransactions.find(pt => pt.closeAccount);
};

/** @returns {DestinationAccount[]} */
function getDestinationAccounts(/** @type {Account} */ account) {
    /** @type {DestinationAccount[]} */
    let mappedAccounts = account.transferAccounts.map((account) => {
        return {
            accountNumber: account.accountNumber,
            sortCode: account.sortCode,
            productName: account.product.name,
            label: `${account.product.name} - ${account.accountNumber}`,
            productType: account.product.productType,
            balance: account.balance
        };
    });

    mappedAccounts.push({
        accountNumber: account.linkedAccountNumber || '',
        sortCode: account.linkedAccountSortCode || '',
        productName: null,
        label: `Linked Account - ${account.linkedAccountNumber}`,
        productType: null,
        balance: null
    });

    return mappedAccounts;
};

/** @returns {FrsProductData} */
function getFrsProductData(/** @type {Account} */ acc, /** @type {Customer<NewAccount>} */ customer) {
    const destinationAccountType = customer.accounts.filter(account => account.id === acc.maturity?.destinationAccountId)[0] 
        ? customer.accounts.filter(account => account.id === acc.maturity?.destinationAccountId)[0].product.productType 
        : null

    const product = acc.maturity?.rolloverAccountProductId
        ? customer.products.find(product => product.id === acc.maturity?.rolloverAccountProductId)
        : customer.products.find(product => product.code === productKeys.FixedRateSaver);

    const getRolloverRate = (/** @type {Account} */ acc) => {
        let rolloverRate = 0;

        if (acc.maturity && acc.maturity.availableRolloverRates && acc.maturity.rolloverAccountProductId) {
            const rolloverProductRate = acc.maturity.availableRolloverRates.find(r => r.productId === acc.maturity?.rolloverAccountProductId);
            if (rolloverProductRate) {
                rolloverRate = rolloverProductRate.rate;
            }
        }

        return rolloverRate;
    };

    /** @type {FrsProductData} */
    const frsProductData = {
        fundingStartDate: acc.fundingWindowStartDate,
        maturityDate: acc.maturity?.maturityDate,
        fundingEndDate: acc.fundingEndDate,
        productTerm: acc.product.productTerm,
        balance: acc.balance,
        instruction: acc.maturity?.maturityInstruction,
        accountId: acc.id,
        maturityWindowCloseDate: acc.maturity?.maturityWindowCloseDate,
        maturityWindowCloseTime: acc.maturity?.maturityWindowCloseTime,
        maturityWindowStatus: acc.maturity?.maturityInstructionWindowStatus,
        interestRate: acc.product.interestRate,
        maxWithdrawalLimit: acc.product.maxWithdrawalLimit,
        rollOverInterestRate: getRolloverRate(acc),
        payawayDestinationAccountType: destinationAccountType,
        calculatedBalanceAtMaturity: acc.maturity?.calculatedBalanceAtMaturity,
        maturingAccount: acc,
        rolloverAccountProduct: product
    }

    return frsProductData;
};

/** @returns {FisaProductData} */
function getFisaProductData(/** @type {Account} */ acc, /** @type {Customer<NewAccount>} */ customer) {
    const destinationAccountType = customer.accounts.filter(account => account.id === acc.maturity?.destinationAccountId)[0]
        ? customer.accounts.filter(account => account.id === acc.maturity?.destinationAccountId)[0].product.productType
        : null

    const product = acc.maturity?.rolloverAccountProductId
        ? customer.products.find(product => product.id === acc.maturity?.rolloverAccountProductId)
        : customer.products.find(product => product.code === productKeys.FixedRateCashISA);

    const getRolloverRate = (/** @type {Account} */ acc) => {
        let rolloverRate = 0;

        if (acc.maturity && acc.maturity.availableRolloverRates && acc.maturity.rolloverAccountProductId) {
            const rolloverProductRate = acc.maturity.availableRolloverRates.find(r => r.productId === acc.maturity?.rolloverAccountProductId);
            if (rolloverProductRate) {
                rolloverRate = rolloverProductRate.rate;
            }
        }

        return rolloverRate;
    };

    /** @type {FisaProductData} */
    const fisaProductData = {
        productName: acc.product.name,
        mobileNumber: customer.mobileNumber,
        fundingStartDate: acc.fundingWindowStartDate,
        maturityDate: acc.maturity?.maturityDate,
        fundingEndDate: acc.fundingEndDate,
        productTerm: acc.product.productTerm,
        balance: acc.balance,
        instruction: acc.maturity?.maturityInstruction,
        accountId: acc.id,
        maturityWindowCloseDate: acc.maturity?.maturityWindowCloseDate,
        maturityWindowCloseTime: acc.maturity?.maturityWindowCloseTime,
        maturityWindowStatus: acc.maturity?.maturityInstructionWindowStatus,
        interestRate: acc.product.interestRate,
        maxWithdrawalLimit: acc.product.maxWithdrawalLimit,
        rollOverInterestRate: getRolloverRate(acc),
        payawayDestinationAccountType: destinationAccountType,
        calculatedBalanceAtMaturity: acc.maturity?.calculatedBalanceAtMaturity,
        maturingAccount: acc,
        rolloverAccountProduct: product,
        accountStatus: acc.accountStatus,
        closureFee: acc.closureFee,
        transferIn: acc.transferIn,
        transferOut: acc.transferOut
    }

    return fisaProductData;
};

function AccountTabGroup(/** @type {AccountTabGroupProps} */ props) {
    const path = window.location.pathname.split('/');
    const accounts = props.accounts.filter(a => a.accountStatus !== "Closed");
    const initialAccount = () => {
        if (props.accounts.filter(account => account.id === path[path.length - 1]).length > 0)
            return props.accounts.filter(account => account.id === path[path.length - 1])[0];
        else
            return accounts[0];
    }

    const [visibleTabHeadingIndex, setVisibleTabHeadingIndex] = useState(accounts.indexOf(initialAccount()));
    const [activeAccountId, setActiveAccountId] = useState(accounts.length > 0 ? initialAccount().id : '');

    const numberOfVisibleTabs = 3;

    const renderTabContent = (/** @type {Account} */ acc) => {
        if (acc.product.productType === productTypes.EasyAccess) {
            return <OfsAccountTab profileData={getOfsProfileData(props.customer, acc)} />
        } else if (acc.product.productType === productTypes.FixedTerm) {
            return <FrsAccountTab profileData={getFrsProductData(acc, props.customer)}
            />
        } else if (acc.product.productType === productTypes.Notice) {
            const pendingTransaction = getPendingTransactionTaggedForClosing(acc);
            if (pendingTransaction) {
                return <NsAccountCancelClose
                    profileData={buildNsCancelCloseData(props.customer, acc, pendingTransaction)} />
            }

            return <NsAccountInfo profileData={getNsProfileData(props.customer, acc)} />
        }
        else if (acc.product.productType === productTypes.FixedRateCashISA) {
            return <FisaAccountTab profileData={getFisaProductData(acc, props.customer)} />
        } else if (acc.product.productType === productTypes.InstantAccessCashISA) {
            return <InstantAccessCashIsaDetails profile={getFisaProductData(acc, props.customer)} />
        }
    };

    const changeTab = (/** @type {React.MouseEvent<HTMLDivElement, MouseEvent>} */ e, /** @type {string} */ accountId) => {
        e.preventDefault();
        setActiveAccountId(accountId);
    };

    const getHeadingAccounts = (/** @type {Account[]} */ accs) => {
        const ac = accs.slice(visibleTabHeadingIndex, visibleTabHeadingIndex + numberOfVisibleTabs);
        return ac.map((acc, index) => {
            return (
                <div
                    className={`tab-group__header${acc.id === activeAccountId ? ' tab-group__header--active' : ''}`}
                    onClick={e => changeTab(e, acc.id)}
                    key={index}>
                    {acc.product.name} ({acc.accountNumber})
                </div>
            );
        });
    };

    const scrollLeft = () => {
        const scrollTo = visibleTabHeadingIndex - 1;
        if (scrollTo < 0) {
            setVisibleTabHeadingIndex(0);
        }

        setVisibleTabHeadingIndex(scrollTo);
    };

    const scrollRight = () => {
        const scrollTo = visibleTabHeadingIndex + 1;
        if (scrollTo < 0) {
            setVisibleTabHeadingIndex(0);
        }

        setVisibleTabHeadingIndex(scrollTo);
    };

    if (accounts && accounts.length === 0)
        return <></>;

    return (
        <div className="tab-group__container">
            <div className="tab-group__header-row-container">
                <div
                    className={`tab-group__header-next-prev${visibleTabHeadingIndex === 0 ? ' tab-group__header-next-prev--hide' : ''}`}
                    onClick={scrollLeft}>
                    <span className="tab-group__header-next-prev--show-desktop">{'<'}</span>
                    <span className="tab-group__header-next-prev--show-mobile">
                        <img src={chevUp} />
                    </span>
                </div>
                <div className="tab-group__header-container">
                    {getHeadingAccounts(accounts)}
                </div>
                <div
                    className={`tab-group__header-next-prev${visibleTabHeadingIndex + numberOfVisibleTabs >= accounts.length ? ' tab-group__header-next-prev--hide' : ''}`}
                    onClick={scrollRight}>
                    <span className="tab-group__header-next-prev--show-desktop">{'>'}</span>
                    <span className="tab-group__header-next-prev--show-mobile">
                        <img src={chevDown} />
                    </span>
                </div>
            </div>
            <div className="tab-group__body-container">
                {accounts.map((acc, index) => {
                    return (
                        <div
                            className={`tab-group__body-item${acc.id === activeAccountId ? ' tab-group__body-item--active' : ''}`}
                            key={index}>
                            {renderTabContent(acc)}
                        </div>
                    );
                })}
            </div>
        </div>
    )
};


    
function MyAccount(/** @type {MyAccountProps} */{featureEnabled}) {
       

    var data = useContext(customerDataContext);

    if (!data || data.loading || !data.data) return <Loading message="We're loading your account details..." />;

    const renderAccounts = (/** @type {CustomerDataContextType} */ data) => {
        if (!data || !data.data || !data.data.customer || !data.data.customer.accounts)
            return null;

        const accounts = featureEnabled('EnableFisaMaturity')
            ? data.data.customer.accounts
            : data.data.customer.accounts.filter(account =>
                account.product.productType !== productTypes.InstantAccessCashISA);

        if (accounts.length === 0)
            return null;

        return <AccountTabGroup
            accounts={accounts}
            customer={data.data.customer}
        />
    };

    const visibleAccountCount = featureEnabled('EnableFisaMaturity')
        ? data.data.customer.accounts.length
        : data.data.customer.accounts.filter(account =>
            account.product.productType !== productTypes.InstantAccessCashISA).length;

    return (
        <div className="my-account my-account--no-left-padding my-account--my-account">
            <h1 className="my-account__title my-account__title--extra-padding">
                {visibleAccountCount > 1 ? "Manage accounts" : "Manage account"}
            </h1>
            <div className="my-account__content my-account__content--small-margin-bottom">
                <div className="my-account__full">
                    {renderAccounts(data)}
                </div>
            </div>
        </div>
    );
};

export default withFeatureFlags(MyAccount);
